import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Project } from "@/types/Project";
import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";

export function useProjects() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isNewUser, setIsNewUser] = useState(false);
  const [activeTab, setActiveTab] = useState("list");

  useEffect(() => {
    const checkNewUser = async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { count, error } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.user.id);
      
      if (!error && count === 0) {
        setIsNewUser(true);
        setActiveTab("new");
      }
    };
    
    checkNewUser();
  }, []);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data.map(project => {
        let pricingComponents;
        let invoiceSettings;
        
        try {
          // Handle JSONB fields that might be objects or strings
          if (project.pricing_components) {
            if (typeof project.pricing_components === 'object') {
              pricingComponents = project.pricing_components;
            } else {
              pricingComponents = JSON.parse(project.pricing_components as string);
            }
          }
          
          if (project.invoice_settings) {
            if (typeof project.invoice_settings === 'object') {
              invoiceSettings = project.invoice_settings;
            } else {
              invoiceSettings = JSON.parse(project.invoice_settings as string);
            }
          }
        } catch (e) {
          console.error("Error parsing project data:", e);
        }
        
        return {
          id: project.id,
          name: project.name,
          description: project.description || undefined,
          isArchived: project.is_archived,
          createdAt: new Date(project.created_at),
          hourlyRate: Number(project.hourly_rate),
          user_id: project.user_id,
          team_id: project.team_id,
          owner_id: project.owner_id,
          is_team_project: project.is_team_project || false,
          pricingComponents,
          invoiceSettings
        };
      });
    }
  });

  const createProject = useMutation({
    mutationFn: async (project: Omit<Project, 'id' | 'createdAt'>) => {
      // Get current auth session to debug
      const { data: authData } = await supabase.auth.getUser();
      console.log('Auth user from mutation:', authData.user?.id);
      console.log('Project user_id:', project.user_id);
      console.log('Project owner_id:', project.owner_id);
      console.log('Creating project with data:', project);

      const insertData = {
        name: project.name,
        description: project.description,
        is_archived: project.isArchived,
        hourly_rate: project.hourlyRate,
        pricing_components: project.pricingComponents as any ?? null,
        invoice_settings: project.invoiceSettings as any ?? null,
        user_id: project.user_id,
        owner_id: project.owner_id,
        team_id: project.team_id || null,
        is_team_project: project.is_team_project
      };
      
      console.log('Inserting into database:', insertData);

      const { error } = await supabase
        .from('projects')
        .insert(insertData);

      if (error) {
        console.error('Database error:', error);
        throw error;
      }
      
      // Project created successfully
      console.log('Project created successfully');
      return insertData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setIsNewUser(false);
      setActiveTab("list");
      toast({
        title: "Project created",
        description: "Your project has been successfully created.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating project",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const archiveProject = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('projects')
        .update({ is_archived: true })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error archiving project",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  return {
    projects,
    isLoading,
    isNewUser,
    activeTab,
    setActiveTab,
    createProject: createProject.mutate,
    createProjectAsync: createProject.mutateAsync,
    archiveProject: archiveProject.mutate
  };
}

export function useDashboardProjects() {
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user.user.id)
        .eq('is_archived', false)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data.map(project => ({
        id: project.id,
        name: project.name,
        description: project.description || undefined,
        isArchived: project.is_archived,
        createdAt: new Date(project.created_at),
        hourlyRate: Number(project.hourly_rate),
        user_id: project.user_id,
        team_id: project.team_id,
        owner_id: project.owner_id,
        is_team_project: project.is_team_project || false
      }));
    }
  });

  return { projects };
}
